import {FC, useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {useDropzone} from 'react-dropzone';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//mui
import {Box, SxProps, Theme} from '@mui/material';
//icons
import {ReactComponent as AddPhotoIcon} from '@/assets/icons/add-photo.svg';
//constants
import {ERROR, IMAGE_PREVIEW_ALT, PRIMARY, TEXT} from '@/utils/constants';
//helpers
import {getImagePreview} from '@/utils/helpers';
//components
import Button from '../../Button';
import InputLabel from '../../Inputs/InputLabel';
import FormHelperText from '../../FormHelperText';
//styles
import {greyUploadImageStyle} from './GreyUploadImage.styles';

interface GreyUploadImageProps {
	field?: FieldInputProps<File>;
	form?: FormikProps<any>;
	label?: string;
	parentClassName?: SxProps<Theme>;
	image?: File | string | null;
	customError?: string;
	disabled?: boolean;
	disableErrorMessage?: boolean;
	onChange?: (file: File | null) => void;
}

const GreyUploadImage: FC<GreyUploadImageProps> = ({
	field,
	form,
	label,
	disabled,
	customError,
	image,
	disableErrorMessage,
	parentClassName,
	onChange,
}) => {
	const [uploadedImage, setUploadedImage] = useState<string>();

	useLayoutEffect(() => {
		if (image) setUploadedImage(getImagePreview(image));
		else if (form && field?.value) setUploadedImage(getImagePreview(field.value));
		else setUploadedImage('');
	}, [field?.value, image]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const image = getImagePreview(acceptedFiles[0]);
		setUploadedImage(image);

		if (form && field) {
			form.setFieldValue(field.name, acceptedFiles[0]);
		} else if (onChange) {
			onChange(acceptedFiles[0]);
		}
	}, []);

	const {isDragAccept, getRootProps, open, getInputProps} = useDropzone({
		onDrop,
		noClick: true,
		noKeyboard: true,
		accept: {
			'image/*': ['.jpeg', '.png'],
		},
	});

	const handleDeleteImage = () => {
		if (!uploadedImage) return;

		if (form && field) {
			form.setFieldValue(field.name, null);
		} else if (onChange) {
			onChange(null);
		}

		setUploadedImage('');
	};

	// const handleDeleteImage = () => uploadedImage && form!.setFieldValue(field!.name, null);

	const errorText = useMemo(() => {
		if (customError) return customError;
		return (field?.name && form?.touched[field?.name] && (form?.errors[field?.name] as string)) || undefined;
	}, [form?.errors, form?.touched, field, customError]);

	return (
		<Box
			{...getRootProps()}
			onClick={handleDeleteImage}
			sx={{
				...parentClassName,
				...greyUploadImageStyle.wrapper,
				...(isDragAccept && greyUploadImageStyle.dragged),
				...(disabled && greyUploadImageStyle.disabled),
				...(!!errorText && greyUploadImageStyle.error),
				...(uploadedImage && greyUploadImageStyle.uploadedHover),
			}}
		>
			<input {...getInputProps()} />
			{label && <InputLabel label={label} error={!!errorText} disabled={disabled} />}

			{uploadedImage ? (
				<Box sx={greyUploadImageStyle.image}>
					<img src={uploadedImage} alt={IMAGE_PREVIEW_ALT} />
				</Box>
			) : (
				<Button
					sx={greyUploadImageStyle.button}
					onClick={open}
					variant={TEXT}
					disabled={disabled}
					color={errorText ? ERROR : PRIMARY}
					disableRipple
					endIcon={<AddPhotoIcon />}
				/>
			)}

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
		</Box>
	);
};

export default GreyUploadImage;
